import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { createParam } from "solito";
import { useRouter } from "solito/router";
import { Link as LinkIcon } from "@tamagui/lucide-icons";
import { FullscreenSpinner } from "@medbillai/ui";
import { homeUrl } from "@medbillai/utils/urls";
import { ErrorScreen } from "../../../components/ErrorScreen";
import { analyticsService } from "../../../lib/analytics";
import { type InviteActivationFailureReason, onboardingVersionTag } from "../../../lib/analytics/types";
import { errorHandling } from "../../../lib/apollo/utils";
import { classifyErrorSource } from "../../../lib/sentry/classifyErrorSource";
import { useSignUpOnboardingStore } from "../../../lib/sign-up-onboarding/signUpOnboardingStore";
import { LoginScreen } from "../login";
import { resolveInviteTokenQuery } from "./queries";
const {
  useParam
} = createParam<{
  inviteToken?: string;
  defaultInviteToken?: string;
}>();
const INVITE_ERROR_MESSAGE = "We couldn't open this invite link. Please use the link from your invite email, or contact support.";
type Gate = {
  status: "loading";
}
// Missing or unresolvable token: stop with an error rather than degrade to
// a plain signup — a user must never think they signed up for their
// benefit and end up with an account that doesn't carry it.
| {
  status: "error";
  reason: InviteActivationFailureReason;
  error?: Error;
}
// Already-redeemed token: log in instead, with the token still threaded so
// the server rejects an account the invite isn't associated with.
| {
  status: "login";
} | {
  status: "signup";
  partnerSlug: string;
};

// Resolves a partner invite and gates the entry: fresh invite → signup (the
// server binds the partner at account creation), redeemed → login, anything
// else → error.
export const ActivateScreen = () => {
  const {
    replace
  } = useRouter();
  const [inviteTokenParam] = useParam("inviteToken");
  const [defaultInviteTokenParam] = useParam("defaultInviteToken");
  // `||` not `??`: a blank `inviteToken` must fall through, since we cannot rely
  // on a landing page omitting the param rather than templating it empty.
  const inviteToken = inviteTokenParam || defaultInviteTokenParam || undefined;
  const {
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore();
  const {
    data,
    loading,
    error
  } = useQuery(resolveInviteTokenQuery, {
    variables: {
      inviteToken: inviteToken ?? ""
    },
    skip: inviteToken === undefined,
    fetchPolicy: "network-only",
    context: errorHandling("caller")
  });

  // Decide the gate once, on the first settled resolve, then freeze it. After
  // the user submits, their own signup redeems the invite — re-gating would
  // bounce them from the verify-email screen into the login branch.
  const [gate, setGate] = useState<Gate>({
    status: "loading"
  });
  useEffect(() => {
    if (gate.status !== "loading") {
      return;
    }
    if (inviteToken === undefined) {
      setGate({
        status: "error",
        reason: "missing_token"
      });
      return;
    }
    if (loading) {
      return;
    }
    const resolution = data?.resolveInviteToken;
    if (error !== undefined || resolution === undefined) {
      setGate({
        status: "error",
        reason: "unresolved",
        error
      });
      return;
    }
    if (resolution.__typename === "RedeemedInviteToken") {
      setGate({
        status: "login"
      });
      return;
    }
    setGate({
      status: "signup",
      partnerSlug: resolution.partner.slug
    });
  }, [gate.status, inviteToken, loading, data, error]);
  useEffect(() => {
    if (gate.status === "signup") {
      setIsSignUpOnboarding(true);
      analyticsService.trackEvent("SignUpOnboardingFlowStarted", {
        onboardingVersion: onboardingVersionTag("v3.0", gate.partnerSlug)
      });
    }
    if (gate.status === "error") {
      analyticsService.trackEvent("InviteActivationFailed", {
        reason: gate.reason
      });
    }
  }, [gate, setIsSignUpOnboarding]);
  if (gate.status === "loading") {
    return <FullscreenSpinner />;
  }
  if (gate.status === "error") {
    return <ErrorScreen title="We couldn't open this invite" message={INVITE_ERROR_MESSAGE} icon={LinkIcon} report={gate.error ? {
      screen: "activate",
      source: classifyErrorSource(gate.error, "apollo"),
      error: gate.error
    } : undefined} action={{
      label: "Go home",
      onPress: () => {
        void replace(homeUrl());
      }
    }} />;
  }
  return <LoginScreen inviteToken={inviteToken} data-sentry-element="LoginScreen" data-sentry-component="ActivateScreen" data-sentry-source-file="ActivateScreen.tsx" />;
};